/**
 * BlockShell — wrapper comum aplicado por todos os blocos. Resolve background,
 * padding e alignment a partir de `block.style`, alem de inserir o container
 * de largura maxima padronizado.
 */

import type { BlockShellProps } from './types';

const PADDING_MAP: Record<NonNullable<BlockShellProps['block']['style']>['paddingTop'] & string, string> = {
  sm: 'py-6 md:py-8',
  md: 'py-10 md:py-16',
  lg: 'py-14 md:py-24',
  xl: 'py-20 md:py-32',
};

const PADDING_TOP_MAP: Record<string, string> = {
  sm: 'pt-6 md:pt-8',
  md: 'pt-10 md:pt-16',
  lg: 'pt-14 md:pt-24',
  xl: 'pt-20 md:pt-32',
};

const PADDING_BOTTOM_MAP: Record<string, string> = {
  sm: 'pb-6 md:pb-8',
  md: 'pb-10 md:pb-16',
  lg: 'pb-14 md:pb-24',
  xl: 'pb-20 md:pb-32',
};

const ALIGN_MAP: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

function backgroundClass(bg: string | undefined, theme: { primaryColor: string; secondaryColor: string; accentColor?: string }): {
  className: string;
  style?: React.CSSProperties;
} {
  switch (bg) {
    case 'primary':
      return { className: 'text-white', style: { backgroundColor: theme.primaryColor } };
    case 'secondary':
      return { className: 'text-white', style: { backgroundColor: theme.secondaryColor } };
    case 'accent':
      return {
        className: '',
        style: { backgroundColor: theme.accentColor || `${theme.primaryColor}15` },
      };
    case 'transparent':
      return { className: '' };
    default:
      return { className: 'bg-white' };
  }
}

export function BlockShell({ block, ctx, children }: BlockShellProps) {
  const style = block.style || {};
  const theme = ctx.tenant.theme;
  const bg = backgroundClass(style.background, theme);

  const paddingClasses: string[] = [];
  if (style.paddingTop && style.paddingBottom) {
    if (style.paddingTop === style.paddingBottom) {
      paddingClasses.push(PADDING_MAP[style.paddingTop] || PADDING_MAP.md);
    } else {
      paddingClasses.push(PADDING_TOP_MAP[style.paddingTop] || PADDING_TOP_MAP.md);
      paddingClasses.push(PADDING_BOTTOM_MAP[style.paddingBottom] || PADDING_BOTTOM_MAP.md);
    }
  } else if (style.paddingTop) {
    paddingClasses.push(PADDING_TOP_MAP[style.paddingTop] || PADDING_TOP_MAP.md);
  } else if (style.paddingBottom) {
    paddingClasses.push(PADDING_BOTTOM_MAP[style.paddingBottom] || PADDING_BOTTOM_MAP.md);
  } else {
    paddingClasses.push(PADDING_MAP.md);
  }

  const alignClass = ALIGN_MAP[style.alignment || 'left'];

  return (
    <section
      data-block-id={block.id}
      data-block-type={block.type}
      className={`${bg.className} ${paddingClasses.join(' ')} ${alignClass}`}
      style={bg.style}
    >
      <div className="container mx-auto px-4 max-w-5xl">{children}</div>
    </section>
  );
}
